import React, { useMemo, useState } from 'react';
import { Mui, MuiIcons } from '@leap4/frontend';
import { failureLabelFor, ISweepLog } from './appAdsLines.interface';
import { dateToStringFormat } from '../../utils/functions';

const {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Chip,
    FormControl,
    MenuItem,
    Select,
    Typography,
} = Mui;
const { ExpandMore: ExpandMoreIcon } = MuiIcons;

/**
 * The outcome of past "Check all" runs, collapsed by default.
 *
 * Exists because the sweep summary was only ever a modal: leaving the page a moment before the
 * run finished lost the result with no way back to it. Individual lines already keep their own
 * change log — what was missing was the across-the-board view, which only a sweep produces.
 *
 * Collapsed by default so it doesn't push the publisher list down the page on every visit.
 */
export const LastCheckPanel: React.FC<{ logs: ISweepLog[] }> = ({ logs }) => {
    const [selectedId, setSelectedId] = useState<string>('');

    const selected = useMemo(
        () => logs.find((l) => l._id === selectedId) || logs[0],
        [logs, selectedId],
    );

    /** Failures grouped by cause, so each group carries one shared instruction. */
    const failuresByKind = useMemo(() => {
        const groups = new Map<string, ISweepLog['results']>();
        (selected?.results || [])
            .filter((r) => !r.ok)
            .forEach((r) => {
                const kind = r.failureKind || 'other';
                if (!groups.has(kind)) groups.set(kind, []);
                groups.get(kind).push(r);
            });
        return Array.from(groups.entries());
    }, [selected]);

    /** Only publishers where something actually moved; the rest is noise across 50 rows. */
    const changed = useMemo(
        () => (selected?.results || []).filter((r) => r.ok && (r.changed ?? 0) > 0),
        [selected],
    );

    if (!logs.length) return null;

    /**
     * The panel only renders when no sweep is in flight, so a row still flagged running is one
     * that never finished — the container was replaced or restarted mid-run. Saying so beats
     * showing partial numbers as though they were the whole picture.
     */
    const interrupted = !!selected.running;

    return (
        <Accordion disableGutters sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="body2" fontWeight={600}>
                        Last check
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {dateToStringFormat(selected.startedAt)}
                    </Typography>
                    <Chip size="small" variant="outlined" label={`${selected.checked} checked`} />
                    {interrupted && (
                        <Chip size="small" color="warning" variant="outlined" label="interrupted" />
                    )}
                    {!!selected.failed && (
                        <Chip size="small" color="error" variant="outlined" label={`${selected.failed} unreadable`} />
                    )}
                    {!!changed.length && (
                        <Chip size="small" color="warning" variant="outlined" label={`${changed.length} changed`} />
                    )}
                </Box>
            </AccordionSummary>

            <AccordionDetails>
                {logs.length > 1 && (
                    <FormControl size="small" sx={{ mb: 2, minWidth: 260 }}>
                        <Select
                            value={selected._id}
                            onChange={(e) => setSelectedId(String(e.target.value))}
                        >
                            {logs.map((l) => (
                                <MenuItem key={l._id} value={l._id}>
                                    {dateToStringFormat(l.startedAt)} — {l.checked} checked
                                    {l.failed ? `, ${l.failed} unreadable` : ''}
                                    {l.running ? ' (interrupted)' : ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {interrupted && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This run didn't finish — the server restarted or was redeployed partway
                        through. The {selected.checked} publisher(s) it got to are up to date; the rest
                        were left untouched. Run <strong>Check all</strong> again to complete it.
                    </Alert>
                )}

                {selected.error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        The check stopped early: {selected.error}
                    </Alert>
                )}

                {!!failuresByKind.length && (
                    <Box mb={2}>
                        <Typography variant="subtitle2" gutterBottom>
                            Needs attention
                        </Typography>
                        {failuresByKind.map(([kind, rows]) => (
                            <Box key={kind} mb={1.5}>
                                <Typography variant="body2" fontWeight={600}>
                                    {failureLabelFor(rows[0].failureLabel, kind)} ({rows.length})
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {rows[0].failureAction}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    {rows.map((r) => r.name).join(', ')}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                {!!changed.length && (
                    <Box mb={1}>
                        <Typography variant="subtitle2" gutterBottom>
                            Status changes
                        </Typography>
                        {changed.map((r) => (
                            <Typography variant="body2" key={r.publisherId}>
                                <strong>{r.name}</strong> — {r.summary}
                            </Typography>
                        ))}
                    </Box>
                )}

                {!failuresByKind.length && !changed.length && !selected.error && !interrupted && (
                    <Typography variant="body2" color="text.secondary">
                        Every publisher read successfully, with no status changes.
                    </Typography>
                )}

                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                    {selected.total} publisher(s) in this run
                    {selected.finishedAt
                        ? `, finished ${dateToStringFormat(selected.finishedAt)}`
                        : ''}
                    . Only whole-portfolio checks are logged here — a single publisher's history is on
                    its own lines.
                </Typography>
            </AccordionDetails>
        </Accordion>
    );
};

export default LastCheckPanel;
